import React, { useState, useEffect } from "react"
import axios from "axios"
import { useNavigate, useParams, Link } from "react-router-dom"
import { FaAngleLeft, FaAngleRight, FaPlus } from 'react-icons/fa'
import Alert from 'react-bootstrap/Alert'
import Form from 'react-bootstrap/Form'
import { apiURL } from 'config/globals'
import Topbar from 'components/Topbar'
import CraftRecordNavigation from 'components/CraftRecordNavigation'
import FormRow from 'components/FormRow'
import { checkAuth } from 'utils/Auth'
import StickyFooter from "components/StickyFooter"
import { Button } from "react-bootstrap"

const CraftRecord5 = () => {
    const [currentUser, setCurrentUser] = useState()
    const [museum, setMuseum] = useState({})
    const [craftRecord, setCraftRecord] = useState({
        type: 'traditional'
    })
    const [form, setForm] = useState()
    const [loadError, setLoadError] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submitError, setSubmitError] = useState('')

    const navigate = useNavigate()
    const { entityId, craftRecordId } = useParams()
    
    useEffect(() => {
        const start = async () => {
            setCurrentUser(await checkAuth(navigate))
            await getMuseum(entityId)
            if (craftRecordId) {
                await getCraftRecord(craftRecordId)
            }
        }
        start()
    }, [])

    const getMuseum = async (id) => {
        try {
            const response = await axios.get(`${apiURL}/get-museum/${id}`)

            if (!response.data.success) {
                setLoadError("Cannot load museum data: " + (response.data.error || response.data))
                return
            }

            setMuseum(response.data.item)
        } catch (error) {
            setLoadError(JSON.stringify(error))
        }
    }

    const getCraftRecord = async (id) => {
        try {
            const response = await axios.get(`${apiURL}/get-craft-record/${id}`)

            if (!response.data.success) {
                setLoadError("Cannot load record data: " + (response.data.error || response.data))
                return
            }

            setCraftRecord(response.data.item)
        } catch (error) {
            setLoadError(JSON.stringify(error))
        }
    }

    const handleChange = (field, value) => {
        setForm({
            ...form,
            [field]: value
        })
    }

    const save = async () => {
        setSubmitError('')
        setIsSubmitting(true)

        const submittedForm = {...form}
        
        try {
            const response = await axios.put(`${apiURL}/update-craft-record/${craftRecord.id}`, submittedForm)
            
            if (!response.data.success) {
                setSubmitError(response.data.error || response.data)
                setIsSubmitting(false)
                return
            }

            setIsSubmitting(false)
        } catch (error) {
            setSubmitError(JSON.stringify(error))
            setIsSubmitting(false)
        }
    }

    const previousPage = () => {
        navigate(`/museum/${museum.id}/documentation/craft-records/${craftRecord.id}/edit-person`)
    }

    const nextPage = () => {
        navigate(`/museum/${museum.id}/documentation/craft-records/${craftRecord.id}/edit-process`)
    }

    return (
        <>
            <Topbar currentUser={currentUser} />
            <main className="full-width" style={{paddingBottom: 300, height: 'fit-content'}}>
                {museum.id &&
                    <>
                        <div>
                            <Link to={`/museum/${museum.id}/documentation/craft-records`} className="no-decoration"><FaAngleLeft /> Back to Craft Records</Link>
                        </div>
                        <header className="mt-2">
                            <div className="float-start">
                                <h1>Craft Record</h1>
                            </div>
                        </header>
                        <CraftRecordNavigation currentTab="materials" museumId={museum.id} craftRecordId={craftRecord.id} />
                        {(!craftRecordId || craftRecord.id) &&
                            <>
                                <h3>Source of the materials</h3>
                                <FormRow
                                    key="materialsFromNatural"
                                    label="From natural environment"
                                    content={
                                        <Form.Control
                                            name="materialsFromNatural"
                                            placeholder="Describe the raw materials, where they were collected or harvested, and who processed them"
                                            defaultValue={craftRecord.materialsFromNatural}
                                            as="textarea"
                                            rows="5"
                                            onChange={e => handleChange(e.target.name, e.target.value)}
                                            onBlur={save}
                                        />
                                    }
                                />
                                <FormRow
                                    key="materialsFromTrade"
                                    label="From trade"
                                    content={
                                        <Form.Control
                                            name="materialsFromTrade"
                                            placeholder="Describe the materials and their source, and if they were purchased or bartered"
                                            defaultValue={craftRecord.materialsFromTrade}
                                            as="textarea"
                                            rows="5"
                                            onChange={e => handleChange(e.target.name, e.target.value)}
                                            onBlur={save}
                                        />
                                    }
                                />
                                <h3>Tools used</h3>
                                <FormRow
                                    key="toolsForManufacture"
                                    label="For manufacture"
                                    content={
                                        <Form.Control
                                            name="toolsForManufacture"
                                            placeholder="Name and describe the tools and how they were obtained. Who made them?"
                                            defaultValue={craftRecord.toolsForManufacture}
                                            as="textarea"
                                            rows="5"
                                            onChange={e => handleChange(e.target.name, e.target.value)}
                                            onBlur={save}
                                        />
                                    }
                                />
                                <FormRow
                                    key="toolsForDecoration"
                                    label="For decoration"
                                    content={
                                        <Form.Control
                                            name="toolsForDecoration"
                                            placeholder="Name and describe the tools and how they were obtained. Who made them?"
                                            defaultValue={craftRecord.toolsForDecoration}
                                            as="textarea"
                                            rows="5"
                                            onChange={e => handleChange(e.target.name, e.target.value)}
                                            onBlur={save}
                                        />
                                    }
                                />
                                <h3>Installations used</h3>
                                <FormRow
                                    key="installations"
                                    label="Installations"
                                    content={
                                        <Form.Control
                                            name="installations"
                                            placeholder="Name the installation (e.g. a water mill, a clay kiln) and its location. Give a short description of the installation"
                                            defaultValue={craftRecord.installations}
                                            as="textarea"
                                            rows="5"
                                            onChange={e => handleChange(e.target.name, e.target.value)}
                                            onBlur={save}
                                        />
                                    }
                                />
                                {submitError && 
                                    <Alert key='submitError' variant='danger'>
                                        {submitError}
                                    </Alert>
                                }
                            </>
                        }
                    </>
                }
                {loadError &&
                    <>
                        <Alert key='loadError' variant='danger'>
                            {loadError}
                        </Alert>
                    </>
                }
                <StickyFooter>
                    <Button size="lg" variant="outline-primary" className="float-start" onClick={previousPage}>
                        <FaAngleLeft /> Previous
                    </Button>
                    <Button size="lg" variant="primary" className="float-end" onClick={nextPage}>
                        Next <FaAngleRight />
                    </Button>
                </StickyFooter>
            </main>
        </>
    )
}

export default CraftRecord5